import { baseApi } from "../api";
import {
  FinancesServerResponseObject,
  FinanceHistoryResponse,
  IncomeExpensesTrendResponse,
  NetWorthProjectionResponse,
  TopExpenseCategoriesResponse,
  TrendPeriod,
  YearOverYearResponse,
} from "./types";

interface GetTopExpenseCategoriesArgs {
  year?: number;
  limit?: number;
}

/**
 * Finances dashboard endpoints. All reads are tagged `Finances` so any write
 * elsewhere that invalidates that tag (see `invalidateTransaction` in
 * transactionsApi, plus account/asset mutations) refetches the dashboard. These
 * endpoints are read-only — the page's transaction writes stay on their own
 * domain mutations and reach the dashboard via the shared `Finances` tag.
 */
export const financesApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getFinances: builder.query<FinancesServerResponseObject, number | void>({
      query: (year) => ({
        url: "/finances",
        params: year === undefined ? undefined : { year },
      }),
      providesTags: ["Finances"],
    }),

    getActivityYears: builder.query<number[], void>({
      query: () => ({ url: "/finances/years" }),
      providesTags: ["Finances"],
    }),

    getFinancesHistory: builder.query<FinanceHistoryResponse, number | void>({
      query: (year) => ({
        url: "/finances/history",
        params: year === undefined ? undefined : { year },
      }),
      providesTags: ["Finances"],
    }),

    getTopExpenseCategories: builder.query<
      TopExpenseCategoriesResponse,
      GetTopExpenseCategoriesArgs | void
    >({
      query: (args) => {
        const { year, limit } = args ?? {};
        const params: Record<string, number> = {};
        if (year !== undefined) {
          params.year = year;
        }
        if (limit !== undefined) {
          params.limit = limit;
        }
        return { url: "/finances/top-expenses", params };
      },
      providesTags: ["Finances"],
    }),

    getIncomeExpensesTrend: builder.query<
      IncomeExpensesTrendResponse,
      TrendPeriod | void
    >({
      query: (period) => ({
        url: "/finances/income-expenses-trend",
        params: period === undefined ? undefined : { period },
      }),
      providesTags: ["Finances"],
    }),

    getNetWorthProjection: builder.query<
      NetWorthProjectionResponse,
      number | void
    >({
      query: (months) => ({
        url: "/finances/projection",
        params: months === undefined ? undefined : { months },
      }),
      providesTags: ["Finances"],
    }),

    getYearOverYear: builder.query<YearOverYearResponse, void>({
      query: () => ({ url: "/finances/year-over-year" }),
      providesTags: ["Finances"],
    }),
  }),
});

export const {
  useGetFinancesQuery,
  useGetActivityYearsQuery,
  useGetFinancesHistoryQuery,
  useGetTopExpenseCategoriesQuery,
  useGetIncomeExpensesTrendQuery,
  useGetNetWorthProjectionQuery,
  useGetYearOverYearQuery,
} = financesApi;
